<template>
  <div
    ref="wrapper"
    class="grow p-1.5 sm:p-4 pt-0 gap-4 relative grid grid-cols-[auto_1fr]"
    data-testid="content-edit-compare"
  >
    <ResizableWrapper
      v-slot="{ ready }"
      height="100%"
      :active="['r']"
      :min-width="28"
      :width="resizableWidth"
      :max-width="width ? width - 32 : undefined"
      class="relative"
    >
      <!-- handle -->
      <teleport v-if="ready" to=".resizable-r">
        <div class="flex items-center justify-center relative h-full">
          <button
            tabindex="-1"
            class="h-12 w-4 bg-primary-200 dark:bg-primary-800 hover:bg-primary dark:hover:bg-primary text-white/70 hover:ring-1 ring-primary ring-offset-1 ring-offset-white dark:ring-offset-slate-800 hover:text-white flex items-center gap-1 px-1.5 rounded-full py-2 duration-200 mb-4"
          ></button>
        </div>
      </teleport>
      <!-- handle end -->
      <div
        class="grid grid-rows-[auto_1fr] p-1 sm:p-4 py-0 sm:py-0 bg-surface-0 dark:bg-surface-800 overflow-hidden absolute inset-0"
        data-testid="original-content"
      >
        <p class="h4 text-center mb-4">Original</p>
        <div class="h-full relative overflow-y-auto no-scrollbar">
          <div class="absolute inset-0">
            <MarkdownText :text="originalText" />
          </div>
        </div>
      </div>
    </ResizableWrapper>
    <div class="relative" data-testid="generated-content">
      <div
        class="grid grid-rows-[auto_1fr] p-1 sm:p-2 py-0 sm:py-0 bg-surface-0 dark:bg-surface-800 overflow-hidden absolute inset-0 duration-300"
        :class="action === 'accept' ? 'sm:pr-40' : ''"
      >
        <p class="h4 text-center mb-4">New</p>
        <div class="max-h-full overflow-y-auto no-scrollbar pb-6 sm:pb-10">
          <MarkdownText :text="alternate" />
        </div>
        <ContentRegenerateActionButtons
          class="absolute bottom-4 left-1/2 transform -translate-x-1/2"
          @accept="acceptOrReject('accept')"
          @reject="acceptOrReject('reject')"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import ResizableWrapper from "@/components/wrapper/ResizableWrapper.vue";
import type { IContentBase } from "@/types/content";
import { get } from "lodash-es";
import ContentRegenerateActionButtons from "./regenerate/ContentRegenerateActionButtons.vue";

const props = defineProps<{
  original: IContentBase;
  alternate: string;
  dataPath: string;
}>();

const emit = defineEmits<{
  accept: [];
  reject: [];
}>();

const action = ref<"accept" | "reject">();
const originalText = computed(() => get(props.original, props.dataPath, ""));
const wrapper = ref<HTMLElement | null>(null);
const { width } = useElementSize(wrapper);

const resizableWidth = computed(() => {
  if (!width.value) return undefined;
  const ratio = isMobile.value ? 1.25 : 2;
  if (action.value === "accept") {
    return 0;
  }
  if (action.value === "reject") {
    return width.value - 200;
  }
  return width.value / ratio;
});

function acceptOrReject(_action: "accept" | "reject") {
  action.value = _action;
  if (_action === "accept") {
    emit("accept");
  } else {
    emit("reject");
  }
}
</script>
